import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useI18n, useLocalized } from '../lib/i18n';
import type { StringKey } from '../lib/i18n';
import { formatDate, formatDateTime, formatGel, formatPoints, formatRate } from '../lib/format';
import {
  useCompleteOrder, useCustomer, useCustomers, useDiscardDraft, useDraft, useDrafts,
  useFloorCar, useLastOrder, useQuote, useSaveDraft, useServices, useAssignees,
} from '../lib/queries';
import { useDebounced } from '../lib/useDebounced';
import { parseMoneyToTetri, tetriToInput } from '../lib/money';
import { fromCompletedOrder } from '../components/Invoice';
import { PrintableInvoice } from '../components/PrintableInvoice';
import { ServicePicker } from '../components/ServicePicker';
import { can, useSession } from '../lib/session';
import type { CompletedOrder } from '../lib/types';
import type { DraftSummary } from '../lib/queries';
import {
  Alert, Amount, Button, Card, EmptyState, Field, Label, MoneyInput, PercentInput, Pill, SectionTitle, Select, Stepper, TierBadge,
} from '../components/ui';

type PaymentMethod = 'CASH' | 'CARD' | 'BANK_TRANSFER' | 'OTHER';

const PAYMENT_LABELS: Record<PaymentMethod, StringKey> = {
  CARD: 'neworder.card', CASH: 'neworder.cash',
  BANK_TRANSFER: 'neworder.transfer', OTHER: 'neworder.other',
};

/** `serviceId` empty means a one-off typed into `customName`. */
interface DraftLine {
  key: string;
  serviceId: string;
  customName: string;
  quantity: number;
  priceText: string;
  performedByAdminId: string | null;
  /** Pay typed for this line; absent means the catalogue's fixed pay applies. */
  payText?: string | undefined;
}

/** The value the service list uses for "something else". */
const ONE_OFF = 'custom';

function CustomerPicker({ onPick }: { onPick: (id: string) => void }) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);
  const { data, isFetching } = useCustomers(debounced);
  const results = (data?.items ?? []).slice(0, 8);

  return (
    <Card>
      <SectionTitle>{t('neworder.whoFor')}</SectionTitle>
      <div className="relative">
        <input
          autoFocus
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('neworder.findPlaceholder')}
          aria-label={t('neworder.findCustomer')}
          className="w-full rounded-xl border border-line bg-raised px-4 py-3 text-ink transition-colors placeholder:text-ink-faint focus:border-brand focus:bg-muted focus:outline-none"
        />
        {isFetching && (
          <span className="absolute right-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        )}
      </div>

      <div className="mt-3 min-h-40 space-y-1.5">
        {results.map((customer) => (
          <button
            key={customer.id}
            onClick={() => onPick(customer.id)}
            className="flex w-full items-center justify-between gap-3 rounded-xl border border-line bg-raised px-4 py-3 text-left transition-colors hover:border-brand/50 hover:bg-muted"
          >
            <span className="min-w-0">
              <span className="block truncate font-medium text-ink">{customer.name}</span>
              <span className="tabular block text-xs text-ink-faint">{customer.phone}</span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              {customer.cashbackAvailableTetri ? (
                <span className="tabular text-xs text-brand">
                  {formatGel(customer.cashbackAvailableTetri)}
                </span>
              ) : null}
              {customer.tierCode && <TierBadge code={customer.tierCode} size="sm" />}
            </span>
          </button>
        ))}
        {debounced && results.length === 0 && !isFetching && (
          <p className="pt-6 text-center text-sm text-ink-dim">
            {t('neworder.nobodyMatches', { query: debounced })}{' '}
            <Link to="/customers" className="text-brand hover:underline">{t('neworder.createCustomer')}</Link>
          </p>
        )}
        {!debounced && (
          <p className="pt-6 text-center text-xs text-ink-faint">
            {t('neworder.startTyping')}
          </p>
        )}
      </div>
    </Card>
  );
}

function Receipt({ result, onNew }: { result: CompletedOrder; onNew: () => void }) {
  const { t } = useI18n();
  return (
    <div className="mx-auto max-w-3xl pt-2">
      {/* The operator's confirmation. It does not go on paper. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-positive/30 bg-positive/5 px-5 py-4">
        <div>
          <p className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-positive">
            {t('neworder.completed')}
          </p>
          <Amount
            value={formatGel(result.totals.amountPayableTetri)}
            className="mt-1 block text-[2rem] font-bold leading-none text-ink"
          />
        </div>

        <div className="flex gap-2">
          <Button onClick={onNew} size="lg">{t('neworder.title')}</Button>
          <Button variant="ghost" size="lg" onClick={() => window.print()}>{t('neworder.print')}</Button>
        </div>
      </div>

      {result.tier.changed && (
        <div className="mb-4">
          <Alert tone="success">
            {t('neworder.tierIsNow', { tier: result.tier.code })}
          </Alert>
        </div>
      )}

      <PrintableInvoice data={fromCompletedOrder(result)} />
    </div>
  );
}

function OpenDrafts({ onResume }: { onResume: (draft: DraftSummary) => void }) {
  const { t } = useI18n();
  const { data } = useDrafts();
  const drafts = data?.items ?? [];
  if (drafts.length === 0) return null;

  return (
    <section className="mt-6">
      <SectionTitle>{t('neworder.savedBaskets')}</SectionTitle>
      <div className="space-y-1.5">
        {drafts.map((draft) => (
          <button
            key={draft.id}
            onClick={() => onResume(draft)}
            className="flex w-full items-center justify-between gap-3 rounded-xl border border-line bg-raised px-3.5 py-2.5 text-left transition-colors hover:border-brand/50"
          >
            <span className="min-w-0">
              <span className="block truncate text-sm text-ink">{draft.customerName}</span>
              <span className="block text-[0.68rem] text-ink-faint">
                {t('neworder.lines', { count: draft.lineCount })} · {formatDateTime(draft.updatedAt)}
              </span>
            </span>
            <span className="tabular shrink-0 text-sm text-ink-dim">
              {formatGel(draft.subtotalTetri)}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

export function NewOrderPage() {
  const { t } = useI18n();
  const localized = useLocalized();
  const { data: session } = useSession();
  const [params, setParams] = useSearchParams();
  const customerId = params.get('customer') ?? '';

  const { data: customer } = useCustomer(customerId || undefined);
  const { data: services } = useServices();
  // The floor's list, not the staff directory: a cashier records who did the work too.
  const assignees = useAssignees(can(session, 'orders.assign'));
  const complete = useCompleteOrder();

  const [lines, setLines] = useState<DraftLine[]>([]);
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [redeemText, setRedeemText] = useState('');
  const [discountBp, setDiscountBp] = useState(0);
  const [method, setMethod] = useState<PaymentMethod>('CARD');
  const [payLater, setPayLater] = useState(false);
  const [result, setResult] = useState<CompletedOrder | null>(null);
  const [orderNote, setOrderNote] = useState('');

  /*
   * A car handed over from the floor. Its lines were agreed at intake and the
   * technicians are already recorded against them, so the till fills in only
   * what the till knows: the prices.
   */
  const acceptedId = params.get('accepted');
  // Handed over now, the handover photographed later from the order's page.
  const handoverLater = params.get('handoverLater') === '1';
  const { data: accepted } = useFloorCar(acceptedId ?? undefined);
  const { data: lastOrder } = useLastOrder(customerId || undefined);

  const draftId = params.get('draft');
  const { data: loadedDraft } = useDraft(draftId);
  const saveDraft = useSaveDraft();
  const discardDraft = useDiscardDraft();

  // A saved basket is poured into the form once. After that the form owns the
  // state, so reloading the draft must not overwrite what is being typed.
  const hydrated = useRef<string | null>(null);
  useEffect(() => {
    if (!loadedDraft || hydrated.current === loadedDraft.id) return;
    hydrated.current = loadedDraft.id;
    setLines(loadedDraft.lines.map((line) => ({
      key: crypto.randomUUID(),
      serviceId: line.serviceId,
      customName: '',
      quantity: line.quantity,
      priceText: line.unitPriceTetri > 0 ? tetriToInput(line.unitPriceTetri) : '',
      performedByAdminId: line.performedByAdminId,
    })));
    setDiscountBp(loadedDraft.discountBp);
    setVehicleId(loadedDraft.vehicleId);
    setOrderNote(loadedDraft.notes ?? '');
    if (loadedDraft.requestedRedemptionTetri > 0) {
      setRedeemText(tetriToInput(loadedDraft.requestedRedemptionTetri));
    }
  }, [loadedDraft]);

  // Same one-shot rule as a saved basket: once poured in, the form owns it.
  const fromFloor = useRef<string | null>(null);
  useEffect(() => {
    if (!accepted || fromFloor.current === accepted.order.id) return;
    fromFloor.current = accepted.order.id;
    setLines(accepted.lines.map((line) => ({
      key: crypto.randomUUID(),
      // A job agreed at the door as a one-off keeps the name it was given
      // there; without this it would arrive at the till as a blank line.
      serviceId: line.serviceId ?? ONE_OFF,
      customName: line.serviceId ? '' : localized(line.name),
      quantity: line.quantity,
      priceText: '',
      performedByAdminId: line.performedByAdminId,
    })));
    setVehicleId(accepted.order.vehicleId);
    setOrderNote(accepted.order.notes ?? '');
  }, [accepted]);

  const mayDiscount = can(session, 'orders.discount');

  useEffect(() => {
    if (customer && customer.vehicles.length > 0 && !vehicleId) {
      setVehicleId(customer.vehicles[0]!.id);
    }
  }, [customer, vehicleId]);

  // A one-off counts only once it has been given a name.
  const counted = useMemo(
    () => lines.filter((line) => (line.serviceId === ONE_OFF ? line.customName.trim() : line.serviceId)),
    [lines],
  );

  const payload = useMemo(() => ({
    customerId,
    lines: counted
      .map((line) => ({
        serviceId: line.serviceId === ONE_OFF ? null : line.serviceId,
        customName: line.serviceId === ONE_OFF ? line.customName.trim() : null,
        quantity: line.quantity,
        unitPriceTetri: parseMoneyToTetri(line.priceText),
        performedByAdminId: line.performedByAdminId,
      })),
    discountBp: mayDiscount ? discountBp : 0,
    requestedRedemptionTetri: parseMoneyToTetri(redeemText),
  }), [customerId, counted, discountBp, redeemText, mayDiscount]);

  const hasPricedLine = payload.lines.some((line) => line.unitPriceTetri > 0);

  const maySeePay = can(session, 'staff.pay');
  /** The catalogue's figure for a line: fixed pay per unit, times how many. */
  const fixedPayText = (line: DraftLine): string => {
    const fixed = services?.items.find((s) => s.id === line.serviceId)?.staffPayTetri;
    return fixed === null || fixed === undefined ? '' : tetriToInput(fixed * line.quantity);
  };
  /*
   * Pay goes only with the posting, never into the quote — it has no bearing
   * on the price, and keying the quote on it would refetch on every digit.
   * A line nobody touched sends nothing, and the server applies the same
   * catalogue figure this screen shows.
   */
  const linesWithPay = () => payload.lines.map((line, index) => {
    const typed = counted[index]?.payText;
    if (!maySeePay || typed === undefined || !line.performedByAdminId) return line;
    return { ...line, staffPayTetri: typed.trim() === '' ? null : parseMoneyToTetri(typed) };
  });

  // Waits for typing to settle, so a four-digit price is one request, not four.
  const debouncedPayload = useDebounced(JSON.stringify(payload), 250);
  const settled = useMemo(
    () => JSON.parse(debouncedPayload) as typeof payload,
    [debouncedPayload],
  );

  const quote = useQuote(
    settled,
    Boolean(settled.customerId) && settled.lines.length > 0,
  );
  const current = quote.data ?? null;

  const quoteError = quote.error instanceof ApiError ? quote.error : null;
  /*
   * Whether the total belongs to the basket as it stands. While it does not,
   * the order cannot be posted: the payment is taken from this total, and
   * posting one basket's money against another is how a till ends up short.
   */
  const inSync = JSON.stringify(payload) === debouncedPayload && !quote.isFetching;
  const completeError = complete.error instanceof ApiError ? complete.error : null;

  if (result) {
    return (
      <Receipt
        result={result}
        onNew={() => { setResult(null); setLines([]); setRedeemText(''); setDiscountBp(0); }}
      />
    );
  }

  if (!customerId) {
    return (
      <div className="mx-auto max-w-xl">
        <h1 className="mb-5 font-display text-[2rem] font-bold tracking-tight text-ink">{t('neworder.title')}</h1>
        <CustomerPicker onPick={(id) => setParams({ customer: id })} />
        <OpenDrafts onResume={(draft) => setParams({ customer: draft.customerId, draft: draft.id })} />
      </div>
    );
  }

  const addLine = () => setLines((prev) => [
    ...prev,
    {
      key: crypto.randomUUID(),
      serviceId: services?.items[0]?.id ?? '',
      customName: '',
      quantity: 1,
      priceText: '',
      performedByAdminId: null,
    },
  ]);

  const repeatable = Boolean(lastOrder?.order?.completedAt) && (lastOrder?.lines.length ?? 0) > 0;

  const repeatLast = () => {
    if (!lastOrder?.lines) return;
    setLines(lastOrder.lines.map((line) => ({
      key: crypto.randomUUID(),
      serviceId: line.serviceId ?? '',
      customName: '',
      quantity: line.quantity,
      // Last time's price, offered rather than imposed — the car may be dirtier.
      priceText: line.unitPriceTetri > 0 ? tetriToInput(line.unitPriceTetri) : '',
      performedByAdminId: line.performedByAdminId,
    })));
    if (lastOrder.order?.vehicleId) setVehicleId(lastOrder.order.vehicleId);
  };

  const patchLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((line) => (line.key === key ? { ...line, ...patch } : line)));

  // Fetching with something already on screen: the figure below is one basket
  // behind, and the panel dims until the new one lands.
  const stale = quote.isFetching;

  const canComplete = Boolean(current) && hasPricedLine && inSync;

  return (
    <div className="mx-auto max-w-5xl pb-24 lg:pb-0">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-[2rem] font-bold leading-tight tracking-tight text-ink">
            {t('neworder.title')}
          </h1>
          {customer && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
              <Link to={`/customers/${customer.id}`} className="font-semibold text-ink hover:text-brand">
                {customer.name}
              </Link>
              <TierBadge code={customer.loyalty.tier.code} size="sm" />
              <span className="tabular text-ink-faint">
                {formatGel(customer.loyalty.cashbackAvailableTetri)} {t('common.cashback')}
              </span>
              <span className="tabular text-ink-faint">
                {formatPoints(customer.loyalty.pointsBalance)} {t('common.pts')}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            loading={saveDraft.isPending}
            disabled={payload.lines.length === 0}
            onClick={() => saveDraft.mutate({
              id: draftId,
              customerId,
              vehicleId,
              lines: payload.lines,
              discountBp: payload.discountBp,
              requestedRedemptionTetri: payload.requestedRedemptionTetri,
              notes: orderNote.trim() || null,
            }, {
              onSuccess: ({ id }) => {
                hydrated.current = id;
                setParams({ customer: customerId, draft: id });
              },
            })}
          >
            {draftId ? t('neworder.updateBasket') : t('neworder.saveForLater')}
          </Button>
          {draftId && (
            <Button
              variant="quiet"
              size="sm"
              loading={discardDraft.isPending}
              onClick={() => discardDraft.mutate(draftId, {
                onSuccess: () => setParams({ customer: customerId }),
              })}
            >
              {t('neworder.discard')}
            </Button>
          )}
          <Button variant="quiet" size="sm" onClick={() => setParams({})}>{t('neworder.changeCustomer')}</Button>
        </div>
      </header>

      {draftId && (
        <p className="-mt-2 mb-4 text-xs text-ink-faint">
          {t('neworder.fromBasket')}
        </p>
      )}

      {accepted && (
        <div className="mb-4">
          <Alert tone="info">
            {t('floor.fromIntake')}
            <span className="tabular ml-1">{accepted.order.reference}</span>
          </Alert>
        </div>
      )}

      {customer?.status === 'BLOCKED' && (
        <div className="mb-4">
          <Alert tone="warning">{t('neworder.blockedWarning')}</Alert>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
        <div className="min-w-0 space-y-4">
          {customer && customer.vehicles.length > 0 && (
            <Card>
              <SectionTitle>{t('neworder.vehicle')}</SectionTitle>
              <div className="flex flex-wrap gap-2">
                {customer.vehicles.map((vehicle) => {
                  const active = vehicleId === vehicle.id;
                  return (
                    <button
                      key={vehicle.id}
                      onClick={() => setVehicleId(vehicle.id)}
                      aria-pressed={active}
                      className={`rounded-xl border px-3.5 py-2.5 text-left text-sm transition-colors ${
                        active
                          ? 'border-brand bg-brand-wash text-ink'
                          : 'border-line bg-raised text-ink-dim hover:border-ink-faint hover:bg-muted'
                      }`}
                    >
                      <span className="block font-medium">{vehicle.make} {vehicle.model}</span>
                      {vehicle.licensePlate && (
                        <span className="tabular block text-[0.7rem] text-ink-faint">
                          {vehicle.licensePlate}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>
          )}

          <Card>
            <SectionTitle action={
              <Button variant="ghost" size="sm" onClick={addLine}>{t('neworder.addServiceShort')}</Button>
            }>{t('neworder.services')}</SectionTitle>

            {lines.length === 0 ? (
              <EmptyState
                title={t('neworder.noServices')}
                action={
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button size="sm" onClick={addLine}>{t('neworder.addFirstService')}</Button>
                    {/* Regulars come back for the same thing; this is the whole
                        order in one tap, still editable afterwards. */}
                    {repeatable && (
                      <Button size="sm" variant="ghost" onClick={repeatLast}>
                        {t('neworder.repeatLast', {
                          date: formatDate(lastOrder!.order!.completedAt!),
                        })}
                      </Button>
                    )}
                  </div>
                }
              />
            ) : (
              <div className="space-y-2.5">
                {lines.map((line) => {
                  const service = services?.items.find((s) => s.id === line.serviceId);
                  const lineTotal = parseMoneyToTetri(line.priceText) * line.quantity;
                  return (
                    <div
                      key={line.key}
                      className="rounded-xl border border-line bg-raised/40 p-3"
                    >
                      <ServicePicker
                        label={t('neworder.service')}
                        services={services?.items ?? []}
                        value={line.serviceId === ONE_OFF
                          ? { kind: 'custom', customName: line.customName }
                          : line.serviceId ? { kind: 'service', serviceId: line.serviceId } : null}
                        onChange={(choice) => patchLine(line.key, choice.kind === 'custom'
                          ? { serviceId: ONE_OFF, customName: choice.customName, payText: undefined }
                          : { serviceId: choice.serviceId, customName: '', payText: undefined })}
                      />

                      {line.serviceId === ONE_OFF && (
                        <div className="mt-3">
                          <Field
                            label={t('neworder.oneOffName')}
                            value={line.customName}
                            autoFocus
                            onChange={(event) => patchLine(line.key, {
                              customName: event.target.value,
                            })}
                          />
                        </div>
                      )}

                      <div className="mt-3 flex items-end gap-2">
                        <Stepper
                          value={line.quantity}
                          onChange={(next) => patchLine(line.key, { quantity: next })}
                        />
                        <div className="min-w-0 flex-1">
                          <MoneyInput
                            label={t('neworder.priceCharged')}
                            placeholder={service?.priceFromTetri ? String(service.priceFromTetri / 100) : '0'}
                            value={line.priceText}
                            onChange={(event) => patchLine(line.key, { priceText: event.target.value })}
                          />
                        </div>
                        <button
                          onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                          aria-label={t('neworder.removeService')}
                          className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-ink-faint transition-colors hover:bg-danger/10 hover:text-danger sm:h-10 sm:w-9"
                        >
                          ✕
                        </button>
                      </div>

                      {assignees(line.performedByAdminId).length > 0 && (
                        <div className="mt-3">
                          <Select
                            label={t('neworder.performedBy')}
                            value={line.performedByAdminId ?? ''}
                            onChange={(event) => patchLine(line.key, {
                              performedByAdminId: event.target.value || null,
                            })}
                          >
                            <option value="">{t('neworder.notRecorded')}</option>
                            {assignees(line.performedByAdminId).map((person) => (
                              <option key={person.id} value={person.id}>
                                {person.firstName} {person.lastName}
                                {person.jobTitle ? ` · ${person.jobTitle}` : ''}
                              </option>
                            ))}
                          </Select>
                        </div>
                      )}

                      {maySeePay && line.performedByAdminId && (
                        <div className="mt-3">
                          <MoneyInput
                            label={t('neworder.staffPay')}
                            value={line.payText ?? fixedPayText(line)}
                            placeholder="0"
                            hint={line.payText === undefined && fixedPayText(line)
                              ? t('neworder.staffPayFixed')
                              : undefined}
                            onChange={(event) => patchLine(line.key, { payText: event.target.value })}
                          />
                        </div>
                      )}

                      <div className="mt-2 flex items-center justify-between border-t border-line/60 pt-2 text-[0.7rem]">
                        <span className="text-ink-faint">
                          {service?.priceFromTetri
                            ? t('neworder.catalogFrom', { price: formatGel(service.priceFromTetri) })
                            : t('neworder.noCatalogPrice')}
                        </span>
                        <span className="tabular font-medium text-ink-dim">
                          {lineTotal > 0 ? formatGel(lineTotal) : '—'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card>
            <SectionTitle>{t('neworder.note')}</SectionTitle>
            <textarea
              value={orderNote}
              onChange={(event) => setOrderNote(event.target.value)}
              rows={2}
              maxLength={1000}
              placeholder={t('neworder.notePlaceholder')}
              className="w-full resize-none rounded-xl border border-line bg-raised px-3.5 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:border-brand focus:outline-none"
            />
          </Card>

          <Card>
            <SectionTitle>{t('neworder.cashbackAndDiscount')}</SectionTitle>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <MoneyInput
                  label={t('neworder.applyCashback')}
                  placeholder="0"
                  value={redeemText}
                  onChange={(event) => setRedeemText(event.target.value)}
                  hint={current ? t('neworder.upTo', { amount: formatGel(current.totals.maxRedeemableTetri) }) : undefined}
                />
                {current && current.totals.maxRedeemableTetri > 0 && (
                  <div className="mt-2 flex gap-1.5">
                    <button
                      onClick={() => setRedeemText(String(current.totals.maxRedeemableTetri / 100))}
                      className="inline-flex min-h-10 items-center rounded-lg border border-brand/35 bg-brand-wash px-3 text-[0.7rem] font-semibold text-brand transition hover:bg-brand/20 sm:min-h-7 sm:px-2.5"
                    >
                      {t('neworder.max', { amount: formatGel(current.totals.maxRedeemableTetri) })}
                    </button>
                    {redeemText && (
                      <button
                        onClick={() => setRedeemText('')}
                        className="inline-flex min-h-10 items-center rounded-lg border border-line px-3 text-[0.7rem] text-ink-faint transition hover:text-ink sm:min-h-7 sm:px-2.5"
                      >
                        {t('neworder.clear')}
                      </button>
                    )}
                  </div>
                )}
              </div>
              {mayDiscount && (
                <PercentInput
                  label={t('neworder.discount')}
                  valueBp={discountBp}
                  onChange={setDiscountBp}
                  hint={current && current.totals.discountTetri > 0
                    ? `= ${formatGel(current.totals.discountTetri)}`
                    : t('neworder.managerApproval')}
                />
              )}
            </div>
          </Card>
        </div>

        <aside className="min-w-0 lg:sticky lg:top-6">
          <Card className="overflow-hidden" padded={false}>
            <div className="flex items-center justify-between border-b border-line px-5 py-3">
              <h2 className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
                {t('neworder.summary')}
              </h2>
              {stale && (
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-brand border-t-transparent" />
              )}
            </div>

            <div className={`transition-opacity duration-150 ${stale ? 'opacity-50' : 'opacity-100'}`}>
              {!current || !hasPricedLine ? (
                <div className="grid place-items-center px-5 py-6 text-center lg:min-h-64 lg:py-8">
                  <p className="text-sm text-ink-faint">
                    {lines.length === 0
                      ? t('neworder.addServiceToSeeTotal')
                      : t('neworder.enterPrice')}
                  </p>
                </div>
              ) : (
                <div className="px-5 py-4">
                  <dl className="space-y-1.5 text-sm">
                    <div className="flex justify-between gap-3">
                      <dt className="text-ink-dim">{t('neworder.subtotal')}</dt>
                      <dd className="tabular text-ink">{formatGel(current.totals.subtotalTetri)}</dd>
                    </div>
                    {current.totals.discountTetri > 0 && (
                      <div className="flex justify-between gap-3">
                        <dt className="text-ink-dim">
                          {t('neworder.discountLine', { percent: current.totals.discountBp / 100 })}
                        </dt>
                        <dd className="tabular text-ink">−{formatGel(current.totals.discountTetri)}</dd>
                      </div>
                    )}
                    {current.totals.redemptionAppliedTetri > 0 && (
                      <div className="flex justify-between gap-3">
                        <dt className="text-ink-dim">{t('neworder.cashbackApplied')}</dt>
                        <dd className="tabular text-brand">
                          −{formatGel(current.totals.redemptionAppliedTetri)}
                        </dd>
                      </div>
                    )}
                  </dl>

                  <div className="mt-4 rounded-xl border border-brand/35 bg-brand-wash px-4 py-3.5">
                    <div className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-brand">
                      {t('neworder.toPay')}
                    </div>
                    <Amount
                      value={formatGel(current.totals.amountPayableTetri)}
                      className="mt-1 block text-[2.1rem] font-bold leading-none text-ink"
                      symbolClassName="text-brand/70"
                    />
                  </div>

                  <dl className="mt-4 space-y-1.5 border-t border-line pt-3.5 text-xs">
                    <div className="flex justify-between gap-3">
                      <dt className="text-ink-faint">
                        {t('neworder.earnsAt', { rate: formatRate(current.tier.cashbackRateBp) })}
                      </dt>
                      <dd className="tabular font-medium text-positive">
                        +{formatGel(current.earns.cashbackEarnedTetri)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-ink-faint">{t('neworder.points')}</dt>
                      <dd className="tabular font-medium text-positive">
                        +{formatPoints(current.earns.pointsEarned)}
                      </dd>
                    </div>
                    {current.after.tierChanged && (
                      <div className="flex justify-between gap-3">
                        <dt className="text-ink-faint">{t('neworder.tierAfter')}</dt>
                        <dd><TierBadge code={current.after.tierCode} size="sm" /></dd>
                      </div>
                    )}
                  </dl>

                  {current.notes.length > 0 && (
                    <ul className="mt-3.5 space-y-1.5 border-t border-line pt-3.5">
                      {current.notes.map((note) => (
                        <li key={note.code} className="flex gap-2 text-[0.7rem] leading-relaxed text-ink-faint">
                          <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-ink-faint" />
                          {note.detail}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-4 border-t border-line pt-3.5">
                    <Label>{t('neworder.payment')}</Label>
                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                      {/* Writing the work down now and taking the money later
                          is routine: the customer went for cash, or a company
                          pays by invoice. */}
                      <button
                        onClick={() => setPayLater((value) => !value)}
                        aria-pressed={payLater}
                        className={`col-span-2 inline-flex min-h-11 items-center justify-center rounded-lg border px-2 text-xs font-semibold transition-colors sm:min-h-10 ${
                          payLater
                            ? 'border-warning bg-warning/10 text-warning'
                            : 'border-line bg-raised text-ink-dim hover:border-ink-faint hover:text-ink'
                        }`}
                      >
                        {t('neworder.payLater')}
                      </button>
                      {!payLater && (Object.keys(PAYMENT_LABELS) as PaymentMethod[]).map((option) => (
                        <button
                          key={option}
                          onClick={() => setMethod(option)}
                          aria-pressed={method === option}
                          className={`inline-flex min-h-11 items-center justify-center rounded-lg border px-2 text-xs font-semibold transition-colors sm:min-h-10 ${
                            method === option
                              ? 'border-brand bg-brand-wash text-brand'
                              : 'border-line bg-raised text-ink-dim hover:border-ink-faint hover:text-ink'
                          }`}
                        >
                          {t(PAYMENT_LABELS[option])}
                        </button>
                      ))}
                    </div>
                  </div>

                  {(completeError || quoteError) && (
                    <div className="mt-3.5">
                      <Alert>{(completeError ?? quoteError)!.message}</Alert>
                    </div>
                  )}

                  <Button
                    size="lg"
                    className="mt-4 hidden w-full lg:inline-flex"
                    disabled={!inSync}
                    loading={complete.isPending}
                    onClick={() => {
                      complete.mutate({
                        ...payload,
                        lines: linesWithPay(),
                        vehicleId,
                        payments: current.totals.amountPayableTetri > 0 && !payLater
                          ? [{ method, amountTetri: current.totals.amountPayableTetri }]
                          : [],
                        draftId,
                        acceptedOrderId: acceptedId,
                        handoverLater,
                        ...(orderNote.trim() ? { notes: orderNote.trim() } : {}),
                      }, { onSuccess: setResult });
                    }}
                  >
                    {payLater
                      ? t('neworder.completeUnpaid')
                      : t('neworder.completeAmount', { amount: formatGel(current.totals.amountPayableTetri) })}
                  </Button>

                  {current.customer.status === 'BLOCKED' && (
                    <p className="mt-2 text-center text-[0.7rem] text-warning">
                      <Pill tone="warning">{t('neworder.blocked')}</Pill> {t('neworder.noCashbackOrPoints')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>
        </aside>
      </div>

      {canComplete && current && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface/95 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-sm lg:hidden">
          <div className="mx-auto flex max-w-5xl items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className={`text-[0.6rem] font-semibold uppercase tracking-[0.18em] ${payLater ? 'text-warning' : 'text-brand'}`}>
                {payLater ? t('neworder.willOwe') : t('neworder.toPay')}
              </div>
              <Amount
                value={formatGel(current.totals.amountPayableTetri)}
                className="block text-2xl font-bold leading-none text-ink"
                symbolClassName="text-brand/70"
              />
            </div>
            <Button
              size="lg"
              className="shrink-0"
              loading={complete.isPending}
              onClick={() => {
                complete.mutate({
                  ...payload,
                  lines: linesWithPay(),
                  vehicleId,
                  payments: current.totals.amountPayableTetri > 0 && !payLater
                    ? [{ method, amountTetri: current.totals.amountPayableTetri }]
                    : [],
                  draftId,
                  acceptedOrderId: acceptedId,
                  handoverLater,
                  ...(orderNote.trim() ? { notes: orderNote.trim() } : {}),
                }, { onSuccess: setResult });
              }}
            >{payLater ? t('neworder.completeUnpaid') : t('neworder.complete')}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
